import { useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const W = 640
const H = 180
const PAD = { top: 12, right: 12, bottom: 24, left: 36 }

function niceDomain(min, max, fixed) {
  if (fixed) return fixed
  if (min === max) return [min - 1, max + 1]
  const pad = (max - min) * 0.1
  return [Math.floor(min - pad), Math.ceil(max + pad)]
}

/**
 * One measure over time: a 2px line, recessive grid, crosshair + tooltip on
 * hover/focus, and a table view. One series per chart (never two y-scales).
 */
export default function TelemetryChart({ title, points, unit = '', domain: fixedDomain, digits = 0 }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const [hover, setHover] = useState(null)
  const svgRef = useRef(null)
  const id = useId()

  const data = useMemo(() => points.filter((p) => p.v != null).map((p) => ({ t: new Date(p.t), v: p.v })), [points])

  const fmtTime = (d) => d.toLocaleString(lang, { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })
  const fmtVal = (v) => `${v.toFixed(digits)}${unit}`

  if (data.length < 2) {
    return (
      <figure className="rounded-2xl border border-line p-4">
        <figcaption className="text-sm font-extrabold">{title}</figcaption>
        <p className="py-10 text-center text-sm text-subtle">{t('admin.chart.empty')}</p>
      </figure>
    )
  }

  const t0 = data[0].t.getTime()
  const t1 = data[data.length - 1].t.getTime()
  const [y0, y1] = niceDomain(Math.min(...data.map((d) => d.v)), Math.max(...data.map((d) => d.v)), fixedDomain)
  const x = (d) => PAD.left + ((d.getTime() - t0) / (t1 - t0 || 1)) * (W - PAD.left - PAD.right)
  const y = (v) => PAD.top + (1 - (v - y0) / (y1 - y0)) * (H - PAD.top - PAD.bottom)
  const path = data.map((d, i) => `${i ? 'L' : 'M'}${x(d.t).toFixed(1)},${y(d.v).toFixed(1)}`).join('')
  const yTicks = [y0, (y0 + y1) / 2, y1]
  const xTicks = [data[0].t, data[Math.floor(data.length / 2)].t, data[data.length - 1].t]

  const pick = (clientX) => {
    const rect = svgRef.current.getBoundingClientRect()
    const px = ((clientX - rect.left) / rect.width) * W
    let best = 0
    for (let i = 1; i < data.length; i++) if (Math.abs(x(data[i].t) - px) < Math.abs(x(data[best].t) - px)) best = i
    setHover(best)
  }
  const onKey = (e) => {
    if (e.key === 'ArrowLeft') setHover((h) => Math.max(0, (h ?? data.length) - 1))
    if (e.key === 'ArrowRight') setHover((h) => Math.min(data.length - 1, (h ?? -1) + 1))
  }
  const hp = hover != null ? data[hover] : null
  const last = data[data.length - 1]

  return (
    <figure className="flex flex-col gap-2 rounded-2xl border border-line p-4">
      <div className="flex items-baseline justify-between gap-2">
        <figcaption id={`${id}-t`} className="text-sm font-extrabold">
          {title}
        </figcaption>
        <span className="text-lg font-black">{fmtVal(last.v)}</span>
      </div>
      <div className="relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="h-auto w-full touch-none outline-none focus-visible:ring-2 focus-visible:ring-teal-ring"
          role="img"
          aria-labelledby={`${id}-t`}
          tabIndex={0}
          onPointerMove={(e) => pick(e.clientX)}
          onPointerLeave={() => setHover(null)}
          onFocus={() => setHover(data.length - 1)}
          onBlur={() => setHover(null)}
          onKeyDown={onKey}
        >
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke="#ECEAE4" />
              <text x={PAD.left - 6} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#5A6672">
                {v.toFixed(digits)}
              </text>
            </g>
          ))}
          {xTicks.map((d, i) => (
            <text
              key={i}
              x={x(d)}
              y={H - 6}
              textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
              fontSize="11"
              fill="#5A6672"
            >
              {d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}
            </text>
          ))}
          <path d={path} fill="none" stroke="#2E7D75" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {hp && (
            <g>
              <line x1={x(hp.t)} x2={x(hp.t)} y1={PAD.top} y2={H - PAD.bottom} stroke="#1E2A35" strokeOpacity="0.35" />
              <circle cx={x(hp.t)} cy={y(hp.v)} r="5" fill="#2E7D75" stroke="#fff" strokeWidth="2" />
            </g>
          )}
        </svg>
        {hp && (
          <div
            role="status"
            className="pointer-events-none absolute top-1 rounded-lg bg-ink px-2.5 py-1.5 text-xs text-on-ink shadow-lg"
            style={{
              left: `${(x(hp.t) / W) * 100}%`,
              transform: x(hp.t) > W * 0.6 ? 'translateX(calc(-100% - 8px))' : 'translateX(8px)',
            }}
          >
            <div className="text-sm font-black">{fmtVal(hp.v)}</div>
            <div className="text-white/70">{fmtTime(hp.t)}</div>
          </div>
        )}
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer font-bold text-subtle">{t('admin.chart.table')}</summary>
        <div className="mt-2 max-h-48 overflow-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-subtle">
                <th className="py-1 font-bold">{t('admin.chart.time')}</th>
                <th className="py-1 text-right font-bold">{title}</th>
              </tr>
            </thead>
            <tbody>
              {[...data].reverse().map((d) => (
                <tr key={d.t.getTime()} className="border-t border-line">
                  <td className="py-1">{fmtTime(d.t)}</td>
                  <td className="py-1 text-right font-bold">{fmtVal(d.v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  )
}
